import axios from 'axios';
import config from '@/data/config';
import { decryptToken, extractTokenFromPersistedBlob } from '@/utils/tokenStorage';
import { promoterChanged } from '@/utils/tokenClaims';
import { dropAllContexts } from '@/utils/navigationContext';
import { dispatchWindowEvent } from '@/utils/windowEvent';
import { checkDeployVersionHeader } from '@/utils/deployVersionWatcher';

/** globals */

const VUEX_STORAGE_KEY = 'vuex';
const REFRESH_TOKEN_ENDPOINT = '/users/refresh-token';
const LOGIN_ENDPOINT = '/users/login';
// endpoints that never need (or should send) an Authorization header from a
// previously stored token — attaching/renewing off a stale token here is what
// races a login's fresh token and overwrites it
const PUBLIC_ENDPOINTS = [REFRESH_TOKEN_ENDPOINT, LOGIN_ENDPOINT];
// two separate keys: PROACTIVE_GATE is a coarse "checked recently?" clock (safe
// to seed at boot); REFRESH_STATE is a short-lived cross-tab "renewing right
// now" signal — merging them would make a boot-time seed look like an in-flight renewal
const PROACTIVE_GATE_KEY = 'token_proactive_gate';
const REFRESH_STATE_KEY = 'token_refresh_state';
// unambiguous session-over signal: token:false is also a never-logged-in tab's resting state
const SESSION_END_KEY = 'session_ended_at';

const REFRESH_DEBOUNCE_DELAY = 500;
const REFRESH_TOKEN_MAX_CONSECUTIVE_FAILURES = 3;
// the refresh endpoint's verdict; it answers 400, not 401. Any other failure means no renewal
const REFRESH_DENIED_STATUSES = [400, 401];
// old failures (e.g. from a JWT blacklist race hours ago) must not keep counting
// against a tab forever — only a burst within this window trips the breaker
const REFRESH_FAILURE_RESET_WINDOW = 10 * 60 * 1000;
// JWT_TTL is 60min (api/config/jwt.php) — half of that leaves a comfortable margin
// before real expiry while cutting refresh frequency (and blacklist-race exposure,
// see CROSS_TAB_LOGOUT_DEBOUNCE above) well below the old 5min interval
const PROACTIVE_REFRESH_INTERVAL = 30 * 60 * 1000;
// must comfortably outlast a slow/unstable backend, not just network latency
const REFRESH_IN_FLIGHT_WINDOW = 8 * 1000;
// must be >= REFRESH_IN_FLIGHT_WINDOW, otherwise a waiting tab always gives up
// right before the window it was told to trust elapses
const CROSS_TAB_WAIT_TIMEOUT = 9 * 1000;
// before reloading on a cross-tab logout signal, give the tab a moment to see
// whether a fresher token lands right after (a blacklist-race false alarm)
const CROSS_TAB_LOGOUT_DEBOUNCE = 2 * 1000;

let consecutiveRefreshFailures = 0;
let lastRefreshFailureAt = 0;
let lastRefreshDenied = false;
let tokenRenewalPromise = null;
let ownLastAttemptAt = 0;
let lastKnownToken = null;
// separate from lastKnownToken: the token:false branch zeroes it before the signal arrives
let hadSession = false;

/** http creation */

const httpV1 = axios.create({
  baseURL: `${config.BASE_API_URL}/v1`,
  headers: {
    'content-type': 'application/json',
  },
});
const httpV2 = axios.create({
  baseURL: `${config.BASE_API_URL}/v2`,
  headers: {
    'content-type': 'application/json',
  },
});

/** methods */

/** Writes the blob `getToken` reads: importing the store cycles through notifications. */
const forgetToken = () => {
  try {
    const persisted = JSON.parse(localStorage.getItem(VUEX_STORAGE_KEY));
    if (!persisted?.app) return;

    persisted.app.token = false;
    localStorage.setItem(VUEX_STORAGE_KEY, JSON.stringify(persisted));
  } catch {
    localStorage.removeItem(VUEX_STORAGE_KEY);
  }
};

/** null, not an Error: every caller tests the return for truthiness, and an Error passes. */
const getToken = () => {
  try {
    const store = JSON.parse(localStorage.getItem(VUEX_STORAGE_KEY));
    const token = store?.app?.token;

    if (typeof token !== 'string' || !token) return null;
    return decryptToken(token);
  } catch {
    return null;
  }
};

/** cross-tab coordination */

const readSharedTimestamp = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key))?.at || 0;
  } catch {
    return 0;
  }
};

const writeSharedTimestamp = (key, timestamp) => {
  try {
    localStorage.setItem(key, JSON.stringify({ at: timestamp }));
  } catch {
    /* noop */
  }
};

/** session end */

/** The in-memory store goes too: the blob alone loses the race to the next persist. */
const clearSessionState = () => {
  lastKnownToken = false;
  hadSession = false;
  dispatchWindowEvent('refreshToken', false);
  forgetToken();
  dropAllContexts();
};

const endSession = () => {
  clearSessionState();
  writeSharedTimestamp(SESSION_END_KEY, Date.now());
};

// resolves once another tab's renewal lands a new token, so this tab avoids
// firing a competing refresh-token call of its own
const waitForCrossTabToken = (timeoutMs) => new Promise((resolve, reject) => {
  const previousToken = getToken();
  let timer;

  const onStorage = (event) => {
    if (event.key !== VUEX_STORAGE_KEY || !event.newValue) return;

    const token = extractTokenFromPersistedBlob(event.newValue);
    if (!token || token === previousToken) return;

    clearTimeout(timer);
    window.removeEventListener('storage', onStorage);
    resolve(token);
  };

  timer = setTimeout(() => {
    window.removeEventListener('storage', onStorage);
    reject(new Error('Cross-tab refresh wait timed out'));
  }, timeoutMs);

  window.addEventListener('storage', onStorage);
});

const requestNewToken = () => {
  const attemptTimestamp = Date.now();
  ownLastAttemptAt = attemptTimestamp;
  writeSharedTimestamp(REFRESH_STATE_KEY, attemptTimestamp);
  writeSharedTimestamp(PROACTIVE_GATE_KEY, attemptTimestamp);

  tokenRenewalPromise = new Promise((resolve, reject) => {
    setTimeout(async () => { // NOSONAR: callback assíncrono intencional
      try {
        const response = await httpV1.post(REFRESH_TOKEN_ENDPOINT);

        consecutiveRefreshFailures = 0;
        lastRefreshDenied = false;
        return resolve(response.data.data);
      } catch (err) {
        consecutiveRefreshFailures += 1;
        lastRefreshFailureAt = Date.now();
        lastRefreshDenied = REFRESH_DENIED_STATUSES.includes(err?.response?.status);
        return reject(err);
      } finally {
        tokenRenewalPromise = null;
      }
    }, REFRESH_DEBOUNCE_DELAY);
  });

  return tokenRenewalPromise;
};

const renewToken = async () => {
  // a burst of failures long ago (e.g. a JWT blacklist race) shouldn't keep
  // arming the breaker against attempts that have nothing to do with it
  if (Date.now() - lastRefreshFailureAt > REFRESH_FAILURE_RESET_WINDOW) {
    consecutiveRefreshFailures = 0;
  }

  if (consecutiveRefreshFailures >= REFRESH_TOKEN_MAX_CONSECUTIVE_FAILURES) {
    // carries the verdict of the burst that armed it: a breaker tripped by a backend
    // that is down must not read as "your token is dead"
    return Promise.reject(Object.assign(new Error('Max attempts reached'), {
      refreshDenied: lastRefreshDenied,
    }));
  }

  if (tokenRenewalPromise) return tokenRenewalPromise; // same-tab dedup

  const sharedLastAttempt = readSharedTimestamp(REFRESH_STATE_KEY);
  const anotherTabIsLikelyRenewing = sharedLastAttempt !== ownLastAttemptAt
    && Date.now() - sharedLastAttempt < REFRESH_IN_FLIGHT_WINDOW;

  if (anotherTabIsLikelyRenewing) {
    try {
      const token = await waitForCrossTabToken(CROSS_TAB_WAIT_TIMEOUT);
      return { token };
    } catch {
      return requestNewToken(); // nobody delivered a token in time — try it ourselves
    }
  }

  return requestNewToken();
};

/** "refused" (session gone) vs "did not happen" (call failed, session untouched). */
const isSessionDenied = (err) => (
  typeof err?.refreshDenied === 'boolean'
    ? err.refreshDenied
    : REFRESH_DENIED_STATUSES.includes(err?.response?.status)
);

const dispatchRefreshTokenEvent = (token) => {
  lastKnownToken = token;
  if (typeof token === 'string' && token) hadSession = true;
  dispatchWindowEvent('refreshToken', token);
};

const isPublicRequest = (requestConfig) => PUBLIC_ENDPOINTS.includes(requestConfig?.url);

const maybeTriggerProactiveRefresh = (requestConfig) => {
  // don't recurse off the refresh call's own request, and don't seed a refresh
  // race off a stale token sitting in storage while the user is logging in
  if (isPublicRequest(requestConfig)) return;

  if (!getToken()) return;

  if (Date.now() - readSharedTimestamp(PROACTIVE_GATE_KEY) < PROACTIVE_REFRESH_INTERVAL) return;

  renewToken()
    .then(({ token: newToken }) => dispatchRefreshTokenEvent(newToken))
    .catch((err) => {
      // never forces logout here — only a reactive 401 means the token is actually invalid
      console.log('%c proactive-refresh-failed ', 'background:orange;color:black;', err);
    });
};

const handleCrossTabSync = (event) => {
  // explicit and unambiguous, so no debounce. Tabs on the previous bundle never write
  // it, which is what the token:false branch below still covers
  if (event.key === SESSION_END_KEY && event.newValue) {
    if (!hadSession) return; // a tab that never had a session has nothing to end

    clearSessionState();
    window.location.replace('/');
    return;
  }

  if (event.key !== VUEX_STORAGE_KEY || !event.newValue) return;

  try {
    const parsed = JSON.parse(event.newValue);

    if (parsed?.app?.token === false) {
      // only a real authenticated->false TRANSITION means "logged out elsewhere".
      // vuex-persist also writes token:false as the normal resting state for a tab
      // that was never logged in — reacting to that would bounce two unauthenticated
      // tabs (e.g. two login-page tabs) into reloading each other forever.
      const wasAuthenticated = typeof lastKnownToken === 'string' && lastKnownToken;
      lastKnownToken = false;

      // don't reload immediately: a JWT blacklist race can make this tab observe
      // a transient token:false right before another tab lands the real renewed
      // token — wait a beat and only bounce if this tab is still unauthenticated
      if (wasAuthenticated) {
        setTimeout(() => {
          if (!getToken()) window.location.replace('/');
        }, CROSS_TAB_LOGOUT_DEBOUNCE);
      }
      return;
    }

    const token = extractTokenFromPersistedBlob(event.newValue);
    if (!token || token === lastKnownToken) return;

    // Every request reads the token from storage, so a promoter switch in another
    // tab already changed what this one sends. Adopting the token silently would
    // leave a screen built for one promoter asking about another.
    if (promoterChanged(lastKnownToken, token)) {
      lastKnownToken = token;
      window.location.reload();
      return;
    }

    dispatchRefreshTokenEvent(token);
  } catch {
    /* noop */
  }
};

/** interceptors :: methods */

const handleRequest = (config) => {
  try {
    // login never needs (or should send) an Authorization header from a
    // previously stored token — see PUBLIC_ENDPOINTS above
    if (config?.url === LOGIN_ENDPOINT) return config;

    const token = getToken();

    if (token) config.headers.Authorization = `Bearer ${token}`;

    maybeTriggerProactiveRefresh(config);

    return config;
  } catch (err) {
    return Promise.reject(err);
  }
};

const handleResponseSuccess = (response) => {
  checkDeployVersionHeader(response?.headers);

  return response;
};

const handleResponseError = async (err) => {
  // checked on error responses too — a 4xx/5xx still carries the header
  checkDeployVersionHeader(err?.response?.headers);

  // a 403 is answered by whoever asked: most GETs here are the shell's and the
  // background's, not a screen's, and this interceptor cannot tell them apart
  if (err?.response?.status !== 401) return Promise.reject(err);

  // no session to renew: this 401 answers a credential check, and is the caller's to handle
  if (isPublicRequest(err?.config)) return Promise.reject(err);

  try {
    const { token } = await renewToken();

    dispatchRefreshTokenEvent(token);

    err.config.headers.Authorization = `Bearer ${token}`;

    return axios.request(err.config);
  } catch (refreshErr) {
    console.log(
      '%c handle-response-error ',
      'background:red;color:white;',
      refreshErr,
    );

    // not renewing is not being logged out: 429, 5xx and dropped connections leave the
    // session standing, and the caller gets its own error back
    if (!isSessionDenied(refreshErr)) return Promise.reject(err);

    // the token goes with it: left behind, the login screen still reads a live session
    endSession();
    window.location.replace('/');

    return Promise.reject(err);
  }
};

/** instances */

httpV1.interceptors.request.use(handleRequest);
httpV2.interceptors.request.use(handleRequest);

httpV1.interceptors.response.use(handleResponseSuccess, handleResponseError);
httpV2.interceptors.response.use(handleResponseSuccess, handleResponseError);

window.addEventListener('storage', handleCrossTabSync);

const initialToken = getToken();
if (initialToken) {
  lastKnownToken = initialToken;
  hadSession = true;
}

// avoids treating a brand new session as "interval already elapsed", which
// would fire a redundant proactive refresh on the very first activity after login
if (!readSharedTimestamp(PROACTIVE_GATE_KEY)) {
  writeSharedTimestamp(PROACTIVE_GATE_KEY, Date.now());
}

export {
  httpV1,
  httpV2,
};
